'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import { image } from 'framer-motion/client';

const highlights = [
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        className="w-7 h-7"
      >
        <path d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
      </svg>
    ),
    title: 'Free Ongkir',
    subtitle: 'Jabodetabek',
    desc: 'Pengiriman gratis ke seluruh area Jabodetabek tanpa minimum order',
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        className="w-7 h-7"
      >
        <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Free Desain',
    subtitle: 'Custom',
    desc: 'Tim desainer berpengalaman siap bantu wujudkan ide dan konsep Anda',
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        className="w-7 h-7"
      >
        <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    title: 'Konsultasi',
    subtitle: 'Teknis Gratis',
    desc: 'Konsultasi material, ketebalan, dan teknik pemotongan terbaik untuk proyek Anda',
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        className="w-7 h-7"
      >
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: '24 Jam',
    subtitle: 'Produksi',
    desc: 'Mesin beroperasi 24 jam untuk memastikan pesanan Anda selesai tepat waktu',
  },
];

const layanan = [
 {
  id: 1,
  title: 'Proses Laser Cutting Metal',
  desc: 'Akurasi tanpa toleransi. Mesin Fiber Laser kami menjamin hasil potong yang rapi, bersih, dan konsisten untuk kebutuhan spareparts mesin serta komponen industri. Minim heat-affected zone, memastikan integritas material tetap terjaga sesuai spesifikasi teknis Anda.',
  image:'/assets/images/AboutContent/mesinlaser.jpg',
  alt: 'Close-up of intricate laser-cut metal panel',
  tag: 'Fiber Laser',
},
  {
    id: 2,
    title: 'Laser Cutting Metal & Potong Plat Besi',
    desc: 'Pemotongan logam presisi tinggi menggunakan mesin fiber laser. Cocok untuk pagar, railing, dan parts mesin dengan hasil rapi dan bersih.',
    image: '/assets/images/fiberlaser.jpeg',
    alt: 'Close-up of intricate laser-cut metal panel',
    tag: 'Fiber Laser',
  },
  {
    id: 3,
    title: 'polymethyl methacrylate',
    desc: 'polymethyl methacrylate merupakan jenis plastik transparan yang menyerupai kaca, lebih ringan, kuat, dan mudah dibentuk, sehingga sering digunakan sebagai pengganti kaca dalam berbagai aplikasi.',
    image: '/assets/images/akrilik.jpg',
    alt: 'Detailed CNC carved wood panel',
    tag: 'polymethyl methacrylate',
  },
  {
    id: 4,
    title: 'CNC Router',
    desc: 'Mesin CNC router untuk pemotongan dan ukir material tebal seperti aluminium, kayu solid dan PVC dengan presisi tinggi.',
    image: '/assets/images/mesincnc.jpg',
    alt: 'CNC router machine working on large wood panel',
    tag: 'CNC Router',
  },
  {
    id: 5,
    title: 'Laser Cut Facade',
    desc: 'teknik pembuatan fasad atau tampak depan bangunan yang menggunakan mesin laser untuk memotong material (seperti logam, ACP, atau plastik) menjadi motif kompleks dan estetis..',
    image: '/assets/images/lcf.jpeg',
    alt: 'Laser cut stainless steel architectural panel',
    tag: 'Laser Cut Facade',
  },
  {
    id: 6,
    title: 'Nomor Rumah & Plakat',
    desc: 'Pembuatan nomor rumah, plakat nama, dan signage custom berbagai material dengan desain elegan dan tahan lama.',
     image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a683307f-1764859880372.png',
    alt: 'Custom laser cut house number sign',
    tag: 'Signage',
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function LayananSection() {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const currentIndex = layanan.findIndex((item) => item.id === selectedId);

  const handleNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    const nextIndex = (currentIndex + 1) % layanan.length;
    setSelectedId(layanan[nextIndex].id);
  }, [currentIndex]);

  const handlePrev = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    const prevIndex = (currentIndex - 1 + layanan.length) % layanan.length;
    setSelectedId(layanan[prevIndex].id);
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedId === null) return;
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') setSelectedId(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, handleNext, handlePrev]);

  return (
    <section className="pt-16 pb-4 md:pt-20 md:pb-6 px-4 md:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="text-xs uppercase tracking-widest text-accent font-semibold mb-3 block">
            Layanan Kami
          </span>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight">
              Solusi Cutting & Engraving
              <br />
              <span className="text-muted-foreground font-light">Untuk Semua Kebutuhan</span>
            </h2>
            <a
              href="https://wa.me/628132282297"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent transition-colors"
            >
              Tanya Layanan Lainnya
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="w-4 h-4"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </motion.div>

        {/* GRID LAYANAN */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {layanan.map((item) => (
            <motion.div
              key={item.id}
              variants={cardVariants}
              onClick={() => setSelectedId(item.id)}
              className="group bg-card rounded-2xl overflow-hidden shadow-soft border border-border card-hover ornament-border cursor-pointer"
            >
              <div className="image-zoom-container h-52 w-full relative overflow-hidden">
              {item.image && (
                <AppImage
                  src={item.image}
                  alt={item.alt || 'Indo Cutting Laser'}
                  fill
                  className="object-cover transition-transform duration-500 hover:scale-110"
                />
              )}
            </div>

              <div className="p-6">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-accent/10 text-accent mb-3">
                  {item.tag}
                </span>
                <h3 className="font-bold text-foreground text-lg leading-tight mb-2 group-hover:text-accent transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* HIGHLIGHT CARDS */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 justify-center items-stretch"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {highlights.map((item, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="group bg-card rounded-2xl p-6 shadow-soft border border-border card-hover cursor-default h-full flex flex-col"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-accent/10 dark:bg-accent/20 flex items-center justify-center text-accent transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-base leading-tight">
                    {item.title}
                  </h3>
                  <span className="text-accent text-sm font-semibold">{item.subtitle}</span>
                </div>
              </div>
              <p className="mt-4 text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* LIGHTBOX / POP-UP MODAL */}
        <AnimatePresence>
          {selectedId !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm"
              onClick={() => setSelectedId(null)}
            >
              {/* Navigation Left */}
              <button
                className="absolute left-4 md:left-8 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all"
                onClick={handlePrev}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>

              {/* Navigation Right */}
              <button
                className="absolute right-4 md:right-8 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all"
                onClick={handleNext}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>

              {/* Close Button */}
              <button
                className="absolute top-4 right-4 z-10 p-2 text-white/50 hover:text-white transition-colors"
                onClick={() => setSelectedId(null)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-8 h-8">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <motion.div
                className="relative max-w-5xl w-full h-full flex flex-col items-center justify-center pointer-events-none"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <div className="relative group/modal pointer-events-auto">
                  <img
                    src={layanan[currentIndex].image}
                    alt={layanan[currentIndex].alt}
                    className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
                  />
                  <div className="mt-6 text-center max-w-3xl">
                    <h3 className="text-2xl font-bold text-white mb-2">{layanan[currentIndex].title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{layanan[currentIndex].desc}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}